import { CircleNotchIcon, PlayIcon, StopIcon, XCircleIcon } from '@phosphor-icons/react'
import { CodeEditor, CodeFile, type EditorError, type EditorWidget, type KeyOverrideFunction } from 'mini-code'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { type AmplitudeInfo, type LevelInfo, type NumberWithParamsInfo, type PatternEventInfo, type SequenceData,
  type TimelineInfo, type WaveformInfo } from './bytecode.ts'
import { dspscript } from './dsp-script.ts'
import { adjustErrorLineNumbers, convertToEditorErrors } from './editor-errors.ts'
import { AmplitudeWidget, NoteWidget, SliderWidget, WaveformWidget } from './editor-widgets.ts'
import { waveformPointersMap } from './editor-widgets.ts'
import { functionDefinitions } from './function-definitions.ts'
import { readSequencerStateFromBuffer } from './lib/sequencer-state-mirror.ts'
import { ScrollingVisualizer } from './ScrollingVisualizer.tsx'
import { useCompiledOutputStore, useEditorSettingsStore, useGlobalBpmStore, usePlaybackStore,
  useUserProfileMenuStore } from './store.ts'
import { useTheme } from './theme.ts'
import { tokenizer } from './tokenizer.ts'
import { SequencerManager } from './ui-sequencer.ts'
import { TimelineManager } from './ui-timeline.ts'
import { errorsAreEqual, normalizeWhitespace } from './util.ts'
import { LevelWidget } from './widgets/level.ts'
import { TimelineWidget } from './widgets/timeline.ts'

const compiler = new BytecodeCompiler()

interface InlineEditorProps {
  codeFile: CodeFile
  errors?: EditorError[]
  errorMessage?: string
  isPlaying?: boolean
  isActive?: boolean
  actuallyPlaying?: boolean
  compileMode: 'key' | 'ctrl+enter'
  onCompile?: (code: string) => Promise<void> | void
  onCompileCheck?: (code: string) => void
  onFlash?: () => void
  showPlayStop?: boolean
  showVisualizer?: boolean
  onPlay?: () => void | Promise<void>
  onStop?: () => void | Promise<void>
  headerContent?: React.ReactNode
  autoHeight?: boolean
  height?: string
  onErrorsChange?: (errors: EditorError[]) => void
  onErrorMessageChange?: (message: string) => void
  patternEvents?: PatternEventInfo[]
  sequences?: SequenceData[]
  numbersWithParams?: NumberWithParamsInfo[]
  amplitudeInfos?: AmplitudeInfo[]
  waveformInfos?: WaveformInfo[]
  levelInfos?: LevelInfo[]
  timelineInfos?: TimelineInfo[]
  contextId?: string
}

export const InlineEditor = memo(function InlineEditor({
  codeFile,
  errors: externalErrors,
  errorMessage: externalErrorMessage,
  isPlaying = false,
  isActive = true,
  actuallyPlaying,
  compileMode,
  onCompile,
  onCompileCheck,
  onFlash,
  showPlayStop = false,
  showVisualizer = false,
  onPlay,
  onStop,
  headerContent,
  autoHeight = false,
  height,
  onErrorsChange,
  onErrorMessageChange,
  patternEvents = [],
  sequences = [],
  numbersWithParams = [],
  amplitudeInfos = [],
  waveformInfos = [],
  levelInfos = [],
  timelineInfos = [],
  contextId,
}: InlineEditorProps) {
  const theme = useTheme()
  const hideFunctionSignatures = useEditorSettingsStore(state => state.hideFunctionSignatures)
  const closeMenu = useUserProfileMenuStore(state => state.closeMenu)
  const [internalErrors, setInternalErrors] = useState<EditorError[]>([])
  const [internalErrorMessage, setInternalErrorMessage] = useState('')
  const [showFlash, setShowFlash] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isProgramLoaded, setIsProgramLoaded] = useState(false)
  const compileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const compileCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSeenCodeRef = useRef<string>('')
  const lastSeenCodeForCheckRef = useRef<string>('')
  const handleCompileRef = useRef<(() => Promise<void>) | null>(null)
  const handleCompileCheckRef = useRef<(() => void) | null>(null)
  const handleCompileWithCodeRef = useRef<((code: string) => Promise<void>) | null>(null)
  const onCompileRef = useRef(onCompile)

  // Sequencer animation refs
  const activeNotesRef = useRef<Map<string, number>>(new Map())
  const currentSampleCounterRef = useRef<number>(0)
  const sequencerManagerRef = useRef<SequencerManager | null>(null)
  const timelineManagerRef = useRef<TimelineManager | null>(null)
  const timelinePositionsRef = useRef<Map<number, any>>(new Map())
  const sequencerPointersRef = useRef<Map<number, number>>(new Map())
  const patternLengthsRef = useRef<Map<number, number>>(new Map())
  const sequencesRef = useRef<SequenceData[]>([])
  const widgetOpacityRef = useRef<Map<string, number>>(new Map())

  const containerRef = useRef<HTMLDivElement | null>(null)
  const codeFileRef = useRef<CodeFile>(codeFile)
  const [, triggerRender] = useState(0)

  const dragStateRef = useRef<{
    key: string
    value: number
    min: number
    max: number
    line: number
    column: number
    length: number
    x: number
    y: number
    width: number
    isDragging: boolean
  } | null>(null)

  const rafRef = useRef<number | null>(null)
  const handlersRef = useRef<{
    move: ((e: PointerEvent) => void) | null
    up: (() => void) | null
  }>({ move: null, up: null })

  useEffect(() => {
    codeFileRef.current = codeFile
  }, [codeFile])

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (handlersRef.current.move) {
        window.removeEventListener('pointermove', handlersRef.current.move)
        window.removeEventListener('pointerup', handlersRef.current.up!)
      }
    }
  }, [])

  useEffect(() => {
    const drag = dragStateRef.current
    if (!drag) return

    // Only clear drag state after mouse is released (handlers are null)
    // This prevents clearing while still dragging, but allows clearing after release
    if (handlersRef.current.move !== null) return

    // Wait a bit after release to ensure compilation has completed
    const timeoutId = setTimeout(() => {
      const currentDrag = dragStateRef.current
      if (!currentDrag) return
      if (handlersRef.current.move !== null) return

      const matchingInfo = numbersWithParams.find(
        info => info.line === currentDrag.line && info.column === currentDrag.column,
      )

      if (!matchingInfo) return

      // Calculate the expected string representation to compare
      // This handles rounding differences in float conversion
      const dragValueStr = currentDrag.value.toFixed(3)
      const infoValueStr = matchingInfo.value.toFixed(3)

      // Only clear if string representations match OR values are very close
      const valuesMatch = dragValueStr === infoValueStr || Math.abs(matchingInfo.value - currentDrag.value) < 0.0001

      if (valuesMatch) {
        dragStateRef.current = null
        triggerRender(n => n + 1)
      }
    }, 100) // Small delay to let compilation settle

    return () => clearTimeout(timeoutId)
  }, [numbersWithParams])

  const errorsRaw = externalErrors ?? internalErrors
  const errorMessage = externalErrorMessage ?? internalErrorMessage
  const stableErrorsRef = useRef(errorsRaw)
  const [errors, setErrorsState] = useState(errorsRaw)

  useEffect(() => {
    if (!errorsAreEqual(errorsRaw, stableErrorsRef.current)) {
      stableErrorsRef.current = errorsRaw
      setErrorsState(errorsRaw)
    }
  }, [errorsRaw])

  const setErrors = useCallback(
    (newErrors: EditorError[]) => {
      if (onErrorsChange) {
        onErrorsChange(newErrors)
      }
      else {
        setInternalErrors(newErrors)
      }
    },
    [onErrorsChange],
  )

  const setErrorMessage = useCallback(
    (message: string) => {
      if (onErrorMessageChange) {
        onErrorMessageChange(message)
      }
      else {
        setInternalErrorMessage(message)
      }
    },
    [onErrorMessageChange],
  )

  const handleCompileCheck = useCallback(() => {
    if (onCompileCheck) {
      onCompileCheck(codeFile.value)
      return
    }

    // Fallback: do internal compile check if onCompileCheck not provided
    try {
      compiler.compile(codeFile.value)
      setErrors([])
      setErrorMessage('')
    }
    catch (err: any) {
      console.error(err)
      const editorErrors = convertToEditorErrors(err, codeFile.value)
      setErrors(editorErrors)
      setErrorMessage(adjustErrorLineNumbers(err.message))
    }
  }, [codeFile, onCompileCheck, setErrors, setErrorMessage])

  handleCompileCheckRef.current = handleCompileCheck

  useEffect(() => {
    onCompileRef.current = onCompile
  }, [onCompile])

  const handleCompile = useCallback(async () => {
    const compileFn = onCompileRef.current
    if (!compileFn) return

    // Always compile to check for errors
    try {
      compiler.compile(codeFile.value)
      setErrors([])
      setErrorMessage('')
    }
    catch (err: any) {
      console.error(err)
      const editorErrors = convertToEditorErrors(err, codeFile.value)
      setErrors(editorErrors)
      setErrorMessage(adjustErrorLineNumbers(err.message))
      return
    }

    // Call onCompile which will handle reloading if needed
    await compileFn(codeFile.value)
  }, [codeFile, setErrors, setErrorMessage])

  const handleCompileWithCode = useCallback(
    async (code: string) => {
      const compileFn = onCompileRef.current
      if (!compileFn) return

      // Always compile to check for errors
      try {
        compiler.compile(code)
        setErrors([])
        setErrorMessage('')
      }
      catch (err: any) {
        console.error(err)
        const editorErrors = convertToEditorErrors(err, code)
        setErrors(editorErrors)
        setErrorMessage(adjustErrorLineNumbers(err.message))
        return
      }

      // Call onCompile which will handle reloading if needed
      await compileFn(code)
    },
    [setErrors, setErrorMessage],
  )

  handleCompileRef.current = handleCompile
  handleCompileWithCodeRef.current = handleCompileWithCode

  const handleKeyOverride: KeyOverrideFunction = useCallback(event => {
    if (event.ctrlKey && event.key === 'r') return false
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault()
      const flashStartTime = Date.now()
      setShowFlash(true)
      window.addEventListener(
        'keyup',
        () => {
          const elapsed = Date.now() - flashStartTime
          const remaining = Math.max(0, 100 - elapsed)
          setTimeout(() => {
            setShowFlash(false)
          }, remaining)
        },
        { once: true },
      )
      if (handleCompileRef.current) {
        handleCompileRef.current()
      }
      return false
    }
    return true
  }, [])

  const handlePointerDown = useCallback(() => {
    closeMenu()
  }, [closeMenu])

  // Auto-compile on edit when compileMode === 'key' and playing
  useEffect(() => {
    if (compileMode !== 'key') return
    if (!isActive) return
    if (!isPlaying) return
    if (!onCompileRef.current) return

    // Reset lastSeenCodeRef when starting to track
    lastSeenCodeRef.current = codeFile.value

    const unsubscribe = codeFile.subscribe(() => {
      const newCode = codeFile.value
      // Early return if code hasn't changed (e.g., just selection/caret change)
      if (lastSeenCodeRef.current === newCode) {
        return
      }

      const normalizedNewCode = normalizeWhitespace(newCode)
      const normalizedLastSeen = normalizeWhitespace(lastSeenCodeRef.current)
      const codeChanged = normalizedLastSeen !== normalizedNewCode

      if (!codeChanged) {
        if (compileTimeoutRef.current) {
          clearTimeout(compileTimeoutRef.current)
          compileTimeoutRef.current = null
        }
        return
      }

      lastSeenCodeRef.current = newCode

      if (compileTimeoutRef.current) {
        clearTimeout(compileTimeoutRef.current)
      }

      // Capture code value at the time the timeout is set to avoid race conditions
      const codeToCompile = newCode

      compileTimeoutRef.current = setTimeout(() => {
        // Use handleCompileWithCode to compile the captured code version
        const compileFn = handleCompileWithCodeRef.current
        if (compileFn) {
          compileFn(codeToCompile)
          // Don't update lastSeenCodeRef here - it's already updated in the subscription
          // Updating it here can cause race conditions with new edits
        }
      }, 250)
    })

    return () => {
      unsubscribe()
      if (compileTimeoutRef.current) {
        clearTimeout(compileTimeoutRef.current)
      }
    }
  }, [compileMode, isActive, isPlaying, codeFile])

  // Compile check on edit (always runs)
  useEffect(() => {
    if (!onCompileCheck) return

    // Reset lastSeenCodeForCheckRef when starting to track
    lastSeenCodeForCheckRef.current = codeFile.value

    const unsubscribe = codeFile.subscribe(() => {
      const newCode = codeFile.value
      // Early return if code hasn't changed (e.g., just selection/caret change)
      if (lastSeenCodeForCheckRef.current === newCode) {
        return
      }

      const normalizedNewCode = normalizeWhitespace(newCode)
      const normalizedLastSeen = normalizeWhitespace(lastSeenCodeForCheckRef.current)
      const codeChanged = normalizedLastSeen !== normalizedNewCode

      if (!codeChanged) {
        if (compileCheckTimeoutRef.current) {
          clearTimeout(compileCheckTimeoutRef.current)
          compileCheckTimeoutRef.current = null
        }
        return
      }

      lastSeenCodeForCheckRef.current = newCode

      if (compileCheckTimeoutRef.current) {
        clearTimeout(compileCheckTimeoutRef.current)
      }

      // Check immediately on every change for responsive error updates
      if (handleCompileCheckRef.current) {
        handleCompileCheckRef.current()
        lastSeenCodeForCheckRef.current = codeFile.value
      }

      // Mark that we've checked (for tracking purposes)
      compileCheckTimeoutRef.current = setTimeout(() => {
        compileCheckTimeoutRef.current = null
      }, 100)
    })

    return () => {
      unsubscribe()
      if (compileCheckTimeoutRef.current) {
        clearTimeout(compileCheckTimeoutRef.current)
      }
    }
  }, [codeFile, onCompileCheck])

  // Initial compile check
  useEffect(() => {
    if (onCompileCheck && handleCompileCheckRef.current) {
      handleCompileCheckRef.current()
    }
  }, [codeFile, onCompileCheck])

  const amplitudeWidgetCacheRef = useRef<Map<string, AmplitudeWidget>>(new Map())
  const waveformWidgetCacheRef = useRef<Map<string, WaveformWidget>>(new Map())
  const levelWidgetCacheRef = useRef<Map<string, LevelWidget>>(new Map())
  const timelineWidgetCacheRef = useRef<Map<string, TimelineWidget>>(new Map())
  const waveformPointersRef = useRef<Map<number, { bufferPtr: number; writePosition: number }>>(new Map())

  useEffect(() => {
    amplitudeWidgetCacheRef.current.clear()
    waveformWidgetCacheRef.current.clear()
    levelWidgetCacheRef.current.clear()
    timelineWidgetCacheRef.current.clear()
  }, [theme, timelineInfos])

  // Create widgets from pattern events and numbers with params
  const widgets = useMemo(() => {
    const ctxId = contextId ?? 'inline'

    const numberWidgets = numbersWithParams.map((info) => {
      const widget = new SliderWidget(
        info,
        theme,
        codeFileRef,
        dragStateRef,
        handlersRef,
        rafRef,
        containerRef,
        triggerRender,
      )
      return widget.toEditorWidget()
    })

    const patternWidgets = patternEvents.map((event) => {
      const widget = new NoteWidget(
        event,
        activeNotesRef,
        patternLengthsRef,
        widgetOpacityRef,
      )
      return widget.toEditorWidget()
    })

    const nextAmplitudeCache = new Map<string, AmplitudeWidget>()
    const amplitudeWidgets = amplitudeInfos.map((info) => {
      const cacheKey = `amplitude-${ctxId}-${info.id}-${info.line}-${info.column}`
      let widget = amplitudeWidgetCacheRef.current.get(cacheKey)
      if (!widget) {
        // For inline editors (TrackList, Tutorial), only animate when this editor is playing and active.
        const shouldAnimate = () => isPlaying && isActive

        widget = new AmplitudeWidget(info, theme, shouldAnimate)
        widget.setContextId(ctxId)
      }
      else {
        widget.setContextId(ctxId)
      }
      nextAmplitudeCache.set(cacheKey, widget)
      return widget.toEditorWidget()
    })
    amplitudeWidgetCacheRef.current = nextAmplitudeCache

    const nextWaveformCache = new Map<string, WaveformWidget>()
    const waveformWidgets = waveformInfos.map((info) => {
      const cacheKey = `waveform-${ctxId}-${info.id}-${info.line}-${info.column}`
      let widget = waveformWidgetCacheRef.current.get(cacheKey)
      if (!widget) {
        const shouldAnimate = () => isPlaying && isActive

        widget = new WaveformWidget(info, theme, shouldAnimate)
        widget.setContextId(ctxId)
      }
      else {
        widget.setContextId(ctxId)
      }
      nextWaveformCache.set(cacheKey, widget)
      return widget.toEditorWidget()
    })
    waveformWidgetCacheRef.current = nextWaveformCache

    const nextLevelCache = new Map<string, LevelWidget>()
    const levelWidgets = levelInfos.map((info) => {
      const cacheKey = `level-${ctxId}-${info.id}-${info.line}-${info.column}`
      let widget = levelWidgetCacheRef.current.get(cacheKey)
      if (!widget) {
        const shouldAnimate = () => isPlaying && isActive

        widget = new LevelWidget(info, theme, shouldAnimate)
        widget.setContextId(ctxId)
      }
      else {
        widget.setContextId(ctxId)
      }
      nextLevelCache.set(cacheKey, widget)
      return widget.toEditorWidget()
    })
    levelWidgetCacheRef.current = nextLevelCache

    const nextTimelineCache = new Map<string, TimelineWidget>()
    const timelineWidgets = timelineInfos.map((info) => {
      const cacheKey = `timeline-${ctxId}-${info.id}-${info.line}-${info.column}-${info.pattern}-${info.beat}-${info.segmentIndex}`
      let widget = timelineWidgetCacheRef.current.get(cacheKey)
      if (!widget) {
        widget = new TimelineWidget(
          info,
          timelinePositionsRef,
        )
        widget.setContextId(ctxId)
      }
      else {
        widget.setInfo(info)
        widget.setContextId(ctxId)
      }
      nextTimelineCache.set(cacheKey, widget)
      return widget.toEditorWidget()
    })
    timelineWidgetCacheRef.current = nextTimelineCache

    return [...numberWidgets, ...patternWidgets, ...amplitudeWidgets, ...waveformWidgets, ...levelWidgets, ...timelineWidgets]
  }, [numbersWithParams, patternEvents, amplitudeInfos, waveformInfos, levelInfos, timelineInfos, theme, contextId, isPlaying,
    isActive])

  // Create a stable reference key for sequences to detect changes
  const sequencesKey = sequences.length > 0
    ? sequences.map(s => `${s.id}-${s.notes.length}`).join(',')
    : ''

  // Create a stable reference key for timelines to detect changes
  const uniqueTimelineKeys = new Map<number, string>()
  for (const info of timelineInfos) {
    uniqueTimelineKeys.set(info.id, `${info.id}-${info.pattern}-${info.beat}`)
  }
  const timelinesKey = uniqueTimelineKeys.size > 0
    ? Array.from(uniqueTimelineKeys.values()).join(',')
    : ''

  // The mono ring analyser is already set up globally

  // Set up SequencerManager and TimelineManager
  useEffect(() => {
    // Initialize timeline manager regardless of playing state (for visualization)
    const audioContext = dspscript.getAudioContext()
    const sampleRate = audioContext?.sampleRate ?? 48000

    // Reuse existing TimelineManager or create new one
    let timelineManager = timelineManagerRef.current
    if (!timelineManager) {
      timelineManager = new TimelineManager(1, sampleRate)
      timelineManagerRef.current = timelineManager
    }

    if (timelineInfos.length > 0) {
      // Create unique timeline configs - only one per unique timeline ID needed
      const uniqueTimelines = new Map<number, typeof timelineInfos[0]>()
      for (const info of timelineInfos) {
        uniqueTimelines.set(info.id, info)
      }
      const timelineConfigs = Array.from(uniqueTimelines.values()).map(info => ({
        id: info.id,
        pattern: info.pattern,
        beat: info.beat,
      }))
      timelineManager.setTimelines(timelineConfigs)
    }

    // Reset timeline manager state to ensure clean start
    timelineManager.reset()

    // Initialize timeline manager counter
    const initSampleCounter = dspscript.getGlobalSampleCounter()
    timelineManager.initializeCounter(initSampleCounter)

    if (!isPlaying || !isActive) {
      activeNotesRef.current.clear()
      sequencerManagerRef.current = null
      patternLengthsRef.current.clear()
      widgetOpacityRef.current.clear()
      timelinePositionsRef.current.clear()
      return
    }

    if (sequences.length === 0) {
      return
    }

    // Clear stale notes when creating new manager (e.g. when switching tracks)
    activeNotesRef.current.clear()
    widgetOpacityRef.current.clear()

    const manager = new SequencerManager(1, sampleRate)
    manager.setSequences(sequences)
    sequencesRef.current = sequences

    const sequenceIds = new Set(sequences.map(s => s.id))
    manager.setEnabledSequences(sequenceIds)
    sequencerManagerRef.current = manager

    // Get BPM directly from WASM (respects program BPM if set, otherwise global BPM)
    const currentBpm = dspscript.getCurrentBpm()

    manager.initializeCounter(initSampleCounter, currentBpm)

    const syncSequencerStates = () => {
      const wasmMemory = dspscript.getWasmMemory()
      if (!wasmMemory || !sequencerManagerRef.current) return

      const wasmStates = new Map<number, {
        patternStartSample: number
        currentIndex: number
        shuffleTriggerCount: number
        shuffledIndices: Int32Array | null
        rotatedIndices: Int32Array | null
      }>()

      for (const [cachedSequenceId, statePtr] of sequencerPointersRef.current.entries()) {
        const state = readSequencerStateFromBuffer(wasmMemory, statePtr)
        if (state) {
          const actualSequenceId = state.sequenceId
          wasmStates.set(actualSequenceId, {
            patternStartSample: state.patternStartSample,
            currentIndex: state.currentIndex,
            shuffleTriggerCount: state.shuffleTriggerCount,
            shuffledIndices: state.shuffledIndices,
            rotatedIndices: state.rotatedIndices,
          })
        }
      }

      if (sequencerManagerRef.current) {
        sequencerManagerRef.current.syncFromWasmStates(wasmStates)
      }
    }

    // Try to sync immediately if pointers are already available (e.g. when switching tabs)
    // This ensures widgets are synchronized from the start
    if (sequencerPointersRef.current.size > 0) {
      syncSequencerStates()
    }

    // Get sequencer pointers via RPC (refresh pointers and sync again)
    dspscript.getSequencerPointers().then(pointers => {
      sequencerPointersRef.current.clear()
      for (const pointer of pointers) {
        const sequenceId = pointer.sequenceId
        const statePtr = pointer.statePtr
        sequencerPointersRef.current.set(sequenceId, statePtr)
      }
      syncSequencerStates()
    }).catch(err => {
      console.error('Failed to get sequencer pointers:', err)
    })

    const patternLengths = new Map<number, number>()
    // Use cycleLength from sequences if available, otherwise calculate from pattern events
    for (const seq of sequences) {
      const cycleLength = (seq as any).cycleLength
      if (cycleLength && cycleLength > 0 && cycleLength < seq.notes.length) {
        patternLengths.set(seq.id, cycleLength)
      }
    }

    const sequenceNoteIndices = new Map<number, Set<number>>()
    for (const pe of patternEvents) {
      if (pe.sequenceId !== undefined && pe.noteIndex !== undefined) {
        if (!sequenceNoteIndices.has(pe.sequenceId)) {
          sequenceNoteIndices.set(pe.sequenceId, new Set())
        }
        sequenceNoteIndices.get(pe.sequenceId)!.add(pe.noteIndex)
      }
    }

    for (const [seqId, noteIndices] of sequenceNoteIndices.entries()) {
      // Only set if not already set from cycleLength
      if (!patternLengths.has(seqId)) {
        const maxIndex = Math.max(...Array.from(noteIndices))
        patternLengths.set(seqId, maxIndex + 1)
      }
    }
    patternLengthsRef.current = patternLengths

    return () => {
      activeNotesRef.current.clear()
      timelinePositionsRef.current.clear()
    }
  }, [isPlaying, isActive, sequencesKey, timelinesKey, patternEvents.length, sequences, timelineInfos])

  // Clean up waveform delay buffers only when waveform IDs actually change
  // Use a stable key to detect actual changes, not just array reference changes
  const amplitudeIdsKey = useMemo(() => {
    return amplitudeInfos.map(w => w.id).sort().join(',')
  }, [amplitudeInfos])

  const waveformIdsKey = useMemo(() => {
    return waveformInfos.map(w => w.id).sort().join(',')
  }, [waveformInfos])

  const levelIdsKey = useMemo(() => {
    return levelInfos.map(w => w.id).sort().join(',')
  }, [levelInfos])

  const prevAmplitudeIdsKeyRef = useRef<string>('')
  const prevWaveformIdsKeyRef = useRef<string>('')
  const prevLevelIdsKeyRef = useRef<string>('')

  useEffect(() => {
    // Only clean up if waveform IDs actually changed (not just array reference)
    if (amplitudeIdsKey !== prevAmplitudeIdsKeyRef.current) {
      const prevIds = prevAmplitudeIdsKeyRef.current ? prevAmplitudeIdsKeyRef.current.split(',').map(Number) : []
      const currentIds = new Set(amplitudeInfos.map(w => w.id))

      // Remove pointers for waveforms that no longer exist
      for (const waveformId of prevIds) {
        if (!currentIds.has(waveformId)) {
          waveformPointersMap.delete(waveformId)
        }
      }

      prevAmplitudeIdsKeyRef.current = amplitudeIdsKey
    }

    if (waveformIdsKey !== prevWaveformIdsKeyRef.current) {
      const prevIds = prevWaveformIdsKeyRef.current ? prevWaveformIdsKeyRef.current.split(',').map(Number) : []
      const currentIds = new Set(waveformInfos.map(w => w.id))

      // Remove pointers for waveforms that no longer exist
      for (const waveformId of prevIds) {
        if (!currentIds.has(waveformId)) {
          waveformPointersMap.delete(waveformId)
        }
      }

      prevWaveformIdsKeyRef.current = waveformIdsKey
    }

    if (levelIdsKey !== prevLevelIdsKeyRef.current) {
      const prevIds = prevLevelIdsKeyRef.current ? prevLevelIdsKeyRef.current.split(',').map(Number) : []
      const currentIds = new Set(levelInfos.map(w => w.id))

      // Remove pointers for waveforms that no longer exist
      for (const waveformId of prevIds) {
        if (!currentIds.has(waveformId)) {
          waveformPointersMap.delete(waveformId)
        }
      }

      prevLevelIdsKeyRef.current = levelIdsKey
    }
  }, [amplitudeIdsKey, amplitudeInfos, waveformIdsKey, waveformInfos, levelIdsKey, levelInfos])

  // Fetch waveform pointers for WASM buffers
  useEffect(() => {
    const fetchWaveformPointers = async () => {
      if (!dspscript.getAudioContext()) return
      try {
        const pointers = await dspscript.getWaveformPointers()
        waveformPointersRef.current.clear()
        waveformPointersMap.clear()
        for (const pointer of pointers) {
          waveformPointersRef.current.set(pointer.waveformId, {
            bufferPtr: pointer.bufferPtr,
            writePosition: pointer.writePosition,
          })
          waveformPointersMap.set(pointer.waveformId, {
            bufferPtr: pointer.bufferPtr,
            writePosition: pointer.writePosition,
          })
        }
      }
      catch (error) {
        console.error('Failed to fetch waveform pointers:', error)
      }
    }

    if (dspscript.getAudioContext()) {
      fetchWaveformPointers()
    }

    // Re-fetch when amplitude or waveform infos change
    const interval = setInterval(fetchWaveformPointers, 1000) // Update every second

    return () => clearInterval(interval)
  }, [amplitudeInfos, waveformInfos])

  // Callback to update sequencer state before each draw
  const handleBeforeDraw = useCallback(() => {
    const currentIsPlaying = usePlaybackStore.getState().isPlaying
    if (!currentIsPlaying || !isActive) {
      return
    }

    // Get BPM directly from WASM (respects program BPM if set, otherwise global BPM)
    const currentBpm = dspscript.getCurrentBpm()
    const globalSampleCounter = dspscript.getGlobalSampleCounter()
    currentSampleCounterRef.current = globalSampleCounter

    // Update timeline positions even if there are no sequences
    if (timelineManagerRef.current) {
      const timelinePositions = timelineManagerRef.current.getAllPositions(globalSampleCounter, currentBpm)
      timelinePositionsRef.current.clear()
      for (const [id, position] of timelinePositions) {
        timelinePositionsRef.current.set(id, position)
      }
    }

    if (!sequencerManagerRef.current) {
      return
    }

    if (sequencesRef.current.length === 0) {
      return
    }

    if (sequencerManagerRef.current) {
      const sequenceIds = new Set(sequencesRef.current.map(s => s.id))
      sequencerManagerRef.current.setEnabledSequences(sequenceIds)
    }

    try {
      const wasmMemory = dspscript.getWasmMemory()
      if (wasmMemory && sequencerManagerRef.current) {
        const wasmStates = new Map<number, {
          patternStartSample: number
          currentIndex: number
          shuffleTriggerCount: number
          shuffledIndices: Int32Array | null
          rotatedIndices: Int32Array | null
        }>()

        for (const [cachedSequenceId, statePtr] of sequencerPointersRef.current.entries()) {
          const state = readSequencerStateFromBuffer(wasmMemory, statePtr)
          if (state) {
            const actualSequenceId = state.sequenceId
            wasmStates.set(actualSequenceId, {
              patternStartSample: state.patternStartSample,
              currentIndex: state.currentIndex,
              shuffleTriggerCount: state.shuffleTriggerCount,
              shuffledIndices: state.shuffledIndices,
              rotatedIndices: state.rotatedIndices,
            })
          }
        }

        if (sequencerManagerRef.current) {
          sequencerManagerRef.current.syncFromWasmStates(wasmStates)
        }
      }

      const noteEvents = sequencerManagerRef.current.update(globalSampleCounter, currentBpm)

      const patternLengths = patternLengthsRef.current
      const sequencesWithNewNotes = new Set<number>()

      for (const event of noteEvents) {
        const patternLength = patternLengths.get(event.sequenceId)
        let wrappedIndex = event.noteIndex
        if (patternLength !== undefined && patternLength > 0) {
          wrappedIndex = event.noteIndex % patternLength
        }

        const activeKey = `${event.sequenceId}-${wrappedIndex}`
        activeNotesRef.current.set(activeKey, event.samplePosition)
        sequencesWithNewNotes.add(event.sequenceId)
      }

      for (const sequenceId of sequencesWithNewNotes) {
        const newNoteKeys = new Set<string>()
        for (const event of noteEvents) {
          if (event.sequenceId === sequenceId) {
            const patternLength = patternLengths.get(event.sequenceId)
            let wrappedIndex = event.noteIndex
            if (patternLength !== undefined && patternLength > 0) {
              wrappedIndex = event.noteIndex % patternLength
            }
            newNoteKeys.add(`${sequenceId}-${wrappedIndex}`)
          }
        }
        for (const key of activeNotesRef.current.keys()) {
          if (key.startsWith(`${sequenceId}-`) && !newNoteKeys.has(key)) {
            activeNotesRef.current.delete(key)
          }
        }
      }

      const currentSequenceIds = new Set(sequencesRef.current.map(s => s.id))
      for (const key of activeNotesRef.current.keys()) {
        const sequenceId = parseInt(key.split('-')[0])
        if (!currentSequenceIds.has(sequenceId)) {
          activeNotesRef.current.delete(key)
        }
      }
    }
    catch (error) {
      console.error('[InlineEditor] Error in beforeDraw callback:', error)
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) {
      setIsProgramLoaded(false)
    }
    else {
      // If track becomes active and audio is playing, program must be loaded externally (e.g., from NavBar play)
      const isCurrentlyPlaying = dspscript.isPlaying()
      if (isCurrentlyPlaying && (actuallyPlaying ?? isPlaying)) {
        setIsProgramLoaded(true)
      }
    }
  }, [isActive, actuallyPlaying, isPlaying])

  // Also check when actuallyPlaying prop changes to true (after NavBar play completes)
  useEffect(() => {
    if (isActive && actuallyPlaying && dspscript.isPlaying()) {
      setIsProgramLoaded(true)
    }
  }, [isActive, actuallyPlaying])

  const handlePlay = useCallback(async () => {
    setIsLoading(true)
    setIsProgramLoaded(false)
    try {
      await onPlay?.()
      // onPlay now awaits loadNewProgram, so program is loaded
      setIsProgramLoaded(true)
      setIsLoading(false)
    }
    catch (err) {
      setIsLoading(false)
      setIsProgramLoaded(false)
      throw err
    }
  }, [onPlay])

  return (
    <div ref={containerRef} className="border border-[#333] overflow-hidden shadow-2xl">
      {(showVisualizer || showPlayStop || headerContent) && (
        <div className="p-3 bg-[#0a0a0a] border-b border-[#333] relative">
          {showVisualizer && (
            <ScrollingVisualizer isPlaying={isProgramLoaded && (actuallyPlaying ?? isPlaying) && isActive}
              scrollStep={3} />
          )}
          <div className="flex flex-row flex-wrap items-center gap-2 relative z-10">
            {showPlayStop && (
              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                {!isPlaying
                  ? (
                    <button
                      onPointerDown={e => {
                        e.stopPropagation()
                        handlePlay()
                      }}
                      className="px-3 h-8 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 flex items-center"
                      title="Play"
                      disabled={isLoading}
                    >
                      {isLoading
                        ? <CircleNotchIcon weight="regular" size={16} className="animate-spin" />
                        : <PlayIcon weight="fill" size={16} />}
                    </button>
                  )
                  : (
                    <button
                      onPointerDown={e => {
                        e.stopPropagation()
                        onStop?.()
                      }}
                      className="px-3 h-8 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 flex items-center"
                      title="Stop"
                    >
                      <StopIcon weight="fill" size={16} />
                    </button>
                  )}
              </div>
            )}
            {headerContent && <div className="flex gap-3 flex-1 items-center">{headerContent}</div>}
          </div>
        </div>
      )}
      <div
        className={`${height ? '' : autoHeight ? 'h-auto' : 'h-[300px]'} relative`}
        style={height ? { height } : undefined}
      >
        {showFlash && (
          <div
            className="absolute inset-0 bg-white pointer-events-none z-[9999]"
            style={{ mixBlendMode: 'difference' }}
          />
        )}
        <CodeEditor
          wordWrap={false}
          gutter
          codeFile={codeFile}
          theme={theme}
          tokenizer={tokenizer}
          functionDefinitions={functionDefinitions}
          errors={errors}
          autoHeight={autoHeight}
          keyOverride={handleKeyOverride}
          hideFunctionSignatures={hideFunctionSignatures}
          onPointerDown={handlePointerDown}
          widgets={widgets}
          isAnimating={isPlaying && isActive}
          onBeforeDraw={handleBeforeDraw}
        />
      </div>
      {(errorMessage || errors.length > 0) && (
        <div className="bg-[#ff4444] text-white px-5 py-3 text-sm border-b border-[#ff6666] flex items-center gap-2 leading-tight">
          <XCircleIcon weight="fill" size={16} />
          <span>
            {errorMessage || errors[0]?.message || 'Compilation error'}
            {errors[0] && (
              <span className="opacity-90 ml-2">
                (at line {errors[0].line + 1}, column {errors[0].startColumn + 1})
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  )
})
